import useRequest from "@/lib/request/useRequest";
import { RecommendationTimeline } from "@/types/recommendation";
import {
    faSatellite,
    faToggleOff,
    faCloud,
    faToggleOn,
    faChartLine,
    faCircle,
    faClipboardCheck,
    faDroplet,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, CardHeader, CardBody } from "@heroui/react";
import dayjs from "dayjs";
import { Fragment, useMemo } from "react";
import NStatusChip from "../common/NStatusChip";
import { useTranslation } from "react-i18next";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { TimelineEvent } from "@/types/enums";

function StatusTimeline({ fieldId }: { fieldId: number }) {
    const { useGetRequest } = useRequest();
    const { t } = useTranslation("home-field");

    const timelineQuery = useGetRequest<RecommendationTimeline[]>({
        url: `/fields/${fieldId}/recommendation_timeline/`,
        enabled: !!fieldId,
        params: {
            start_date: getTodayWithTimeTravel()
                .subtract(4, "month")
                .format("YYYY-MM-DD"),
        },
    });

    const timelineData = useMemo(() => {
        return (
            timelineQuery.data?.map((item) => {
                return {
                    ...item,
                    icon:
                        item.event === TimelineEvent.InsightsEnabled
                            ? faToggleOn
                            : item.event === TimelineEvent.InsightsDisabled
                                ? faToggleOff
                                : item.event === TimelineEvent.ImageryGap
                                    ? faCloud
                                    : item.event === TimelineEvent.NewImagery
                                        ? faSatellite
                                        : item.event === TimelineEvent.NewNStatus
                                            ? faChartLine
                                            : item.event === TimelineEvent.ApplicationLogged
                                                ? faClipboardCheck
                                                : item.event === TimelineEvent.MoistureStatusChanged
                                                    ? faDroplet
                                                    : faCircle,
                };
            }) ?? []
        );
    }, [timelineQuery.data]);

    const groupedTimelineData = useMemo(() => {
        const groups: { [key: string]: typeof timelineData } = {};

        timelineData.forEach((item) => {
            const month = dayjs(item.date).format("MMMM YYYY");
            if (!groups[month]) {
                groups[month] = [];
            }
            groups[month].push(item);
        });

        return Object.entries(groups).sort(
            ([a], [b]) =>
                dayjs(b, "MMMM YYYY").valueOf() -
                dayjs(a, "MMMM YYYY").valueOf(),
        );
    }, [timelineData]);

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="font-semibold text-lg">
                {t("event_timeline")}{" "}
                <span className="text-sm text-gray-500">
                    {t("past_four_months")}
                </span>
            </div>
            {groupedTimelineData.map(([month, items]) => (
                <Card key={month} className="flex flex-col gap-2">
                    <CardHeader>
                        <div className="font-semibold text-lg">{month}</div>
                    </CardHeader>
                    <CardBody>
                        <div className="grid grid-cols-4 gap-2 text-sm">
                            {items.map((item, index) => {
                                return (
                                    <Fragment key={index}>
                                        <FontAwesomeIcon
                                            icon={item.icon}
                                            size="2x"
                                            className="text-primary-500"
                                        />
                                        <div>
                                            {dayjs(item.date).format("ll")}
                                        </div>
                                        <div className="text-lg">
                                            {item.event}
                                        </div>
                                        <div>
                                            {item.image_n_status &&
                                                item.event != "Imagery Gap" && (
                                                    <NStatusChip
                                                        nStatus={
                                                            item.image_n_status
                                                        }
                                                    />
                                                )}
                                            {item.n_status &&
                                                item.event != "Imagery Gap" && (
                                                    <NStatusChip
                                                        nStatus={item.n_status}
                                                    />
                                                )}
                                        </div>
                                    </Fragment>
                                );
                            })}
                        </div>
                    </CardBody>
                </Card>
            ))}
        </div>
    );
}

export default StatusTimeline;
